import {
  formatLocalDate,
  fromApiMoney,
  type Instant,
  type LocalDate,
  type Money,
  parseInstant,
  parseLocalDate,
} from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, type QueryValue, queryFingerprint } from '../http/query.ts'
import type { Page } from './page.ts'

export type AccountClass = Schemas['AccountResponse']['accountClass']
export type AccountType = Schemas['AccountResponse']['type']
export type LocalizedName = Schemas['LocalizedNameResponse']

export interface Account {
  readonly id: string
  readonly code: string
  readonly accountClass: AccountClass
  readonly type: AccountType
  readonly names: Readonly<Record<string, LocalizedName>>
  readonly currency: Schemas['AccountResponse']['currency'] | undefined
  readonly parentId: string | undefined
  readonly icon: string
  readonly color: string
  /** Server-derived (groups and asset/liability parents are never postable); it can change under an open form. */
  readonly isPostable: boolean
  /** A parent-only asset or liability account, postable never, even while empty. */
  readonly isGroup: boolean
  readonly isSystem: boolean
  readonly hidden: boolean
  readonly createdAt: Instant
  readonly updatedAt: Instant
  readonly archivedAt: Instant | undefined
}

export interface AccountNode {
  readonly account: Account
  readonly children: readonly AccountNode[]
}

export type AccountListQuery = OperationQuery<'accounts_list'>

const ACCOUNT_LIST_PROJECTION: QueryProjection<AccountListQuery> = {
  limit: (value) => String(value),
  cursor: (value) => value,
  class: (value) => value,
  type: (value) => value,
  archived: (value) => String(value),
  hidden: (value) => String(value),
  search: (value) => value,
}

export type FlowBucket = NonNullable<OperationQuery<'accounts_flows'>['bucket']>

export interface FlowTotals {
  readonly count: number
  readonly inflow: Money
  readonly outflow: Money
  readonly transfersIn: Money
  readonly transfersOut: Money
  readonly adjustments: Money
  readonly net: Money
}

export interface AccountFlowPeriod extends FlowTotals {
  readonly periodStart: LocalDate
  readonly periodEnd: LocalDate
}

export interface AccountFlows {
  readonly displayCurrency: string
  readonly totals: FlowTotals
  readonly periods: readonly AccountFlowPeriod[]
}

export interface AccountFlowsQuery {
  readonly bucket: FlowBucket
  readonly from?: LocalDate | undefined
  readonly until?: LocalDate | undefined
  readonly displayCurrency: NonNullable<OperationQuery<'accounts_flows'>['displayCurrency']>
}

function toFlowTotals(wire: Schemas['AccountFlowTotalsResponse']): FlowTotals {
  return {
    count: wire.count,
    inflow: fromApiMoney(wire.inflow),
    outflow: fromApiMoney(wire.outflow),
    transfersIn: fromApiMoney(wire.transfersIn),
    transfersOut: fromApiMoney(wire.transfersOut),
    adjustments: fromApiMoney(wire.adjustments),
    net: fromApiMoney(wire.net),
  }
}

function toAccountFlows(wire: Schemas['AccountFlowsResponse']): AccountFlows {
  return {
    displayCurrency: wire.displayCurrency,
    totals: toFlowTotals(wire.totals),
    periods: wire.periods.map((period) => ({
      ...toFlowTotals(period),
      periodStart: parseLocalDate(period.periodStart),
      periodEnd: parseLocalDate(period.periodEnd),
    })),
  }
}

function flowsParams(query: AccountFlowsQuery): Record<string, QueryValue> {
  return {
    bucket: query.bucket,
    displayCurrency: query.displayCurrency,
    ...(query.from === undefined ? {} : { from: formatLocalDate(query.from) }),
    ...(query.until === undefined ? {} : { until: formatLocalDate(query.until) }),
  }
}

export const accountKeys = {
  list: (ledgerId: string, query: AccountListQuery): CacheKey =>
    ledgerKey(ledgerId, 'accounts', 'list', queryFingerprint(projectQuery(query, ACCOUNT_LIST_PROJECTION))),
  tree: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'accounts', 'tree'),
  detail: (ledgerId: string, accountId: string): CacheKey =>
    ledgerKey(ledgerId, 'accounts', 'detail', accountId),
  /** Derived from the journal, so keyed under `entries` and dropped by every journal write. */
  flows: (ledgerId: string, accountId: string, query: AccountFlowsQuery): CacheKey =>
    ledgerKey(ledgerId, 'entries', 'account-flows', accountId, queryFingerprint(flowsParams(query))),
  /** Everything a write to the chart of accounts makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'accounts')],
} as const

export interface AccountsRepository {
  list(ledgerId: string, query?: AccountListQuery, signal?: AbortSignal): Promise<Page<Account>>
  tree(ledgerId: string, signal?: AbortSignal): Promise<readonly AccountNode[]>
  get(ledgerId: string, accountId: string, signal?: AbortSignal): Promise<Account>
  /** Money through one account per period, in `displayCurrency`. */
  flows(
    ledgerId: string,
    accountId: string,
    query: AccountFlowsQuery,
    signal?: AbortSignal,
  ): Promise<AccountFlows>
  create(ledgerId: string, input: Schemas['CreateAccountRequest']): Promise<Account>
  /** Rename, recolour or re-icon - gated on ACCOUNT_CREATE (contract/conventions.md §0). */
  update(ledgerId: string, accountId: string, input: Schemas['UpdateAccountRequest']): Promise<Account>
  archive(ledgerId: string, accountId: string): Promise<Account>
  /** 204 No Content on the wire (contract/openapi.json) - unlike archive, there is no body to map. */
  restore(ledgerId: string, accountId: string): Promise<void>
  /** Both 204 No Content, gated on ACCOUNT_CREATE like update (contract/conventions.md §0). */
  hide(ledgerId: string, accountId: string): Promise<void>
  unhide(ledgerId: string, accountId: string): Promise<void>
}

/**
 * The reference repository. Every other one is written this way: it owns its paths, its
 * query projection, its cache keys and the mapping from contract types to domain types,
 * and nothing outside this package builds a URL or reads a raw response.
 */
export function createAccountsRepository(client: TalerClient): AccountsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/accounts`

  return {
    async list(ledgerId, query = {}, signal) {
      const page = await client.call<Schemas['PageResponseAccountResponse']>({
        method: 'GET',
        path: base(ledgerId),
        query: projectQuery(query, ACCOUNT_LIST_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return { items: page.items.map(toAccount), nextCursor: page.nextCursor }
    },

    async tree(ledgerId, signal) {
      const nodes = await client.call<Schemas['AccountNode'][]>({
        method: 'GET',
        path: `${base(ledgerId)}/tree`,
        ...(signal === undefined ? {} : { signal }),
      })
      return nodes.map(toAccountNode)
    },

    async get(ledgerId, accountId, signal) {
      return toAccount(
        await client.call<Schemas['AccountResponse']>({
          method: 'GET',
          path: `${base(ledgerId)}/${encodeURIComponent(accountId)}`,
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async flows(ledgerId, accountId, query, signal) {
      return toAccountFlows(
        await client.call<Schemas['AccountFlowsResponse']>({
          method: 'GET',
          path: `${base(ledgerId)}/${encodeURIComponent(accountId)}/flows`,
          query: flowsParams(query),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      return toAccount(
        await client.call<Schemas['AccountResponse'], Schemas['CreateAccountRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: input,
        }),
      )
    },

    async update(ledgerId, accountId, input) {
      return toAccount(
        await client.call<Schemas['AccountResponse'], Schemas['UpdateAccountRequest']>({
          method: 'PATCH',
          path: `${base(ledgerId)}/${encodeURIComponent(accountId)}`,
          body: input,
        }),
      )
    },

    async archive(ledgerId, accountId) {
      return toAccount(
        await client.call<Schemas['AccountResponse'], Record<string, never>>({
          method: 'POST',
          path: `${base(ledgerId)}/${encodeURIComponent(accountId)}/archive`,
          body: {},
        }),
      )
    },

    async restore(ledgerId, accountId) {
      await client.call<undefined, Record<string, never>>({
        method: 'POST',
        path: `${base(ledgerId)}/${encodeURIComponent(accountId)}/restore`,
        body: {},
      })
    },

    async hide(ledgerId, accountId) {
      await client.call<undefined, Record<string, never>>({
        method: 'POST',
        path: `${base(ledgerId)}/${encodeURIComponent(accountId)}/hide`,
        body: {},
      })
    },

    async unhide(ledgerId, accountId) {
      await client.call<undefined, Record<string, never>>({
        method: 'POST',
        path: `${base(ledgerId)}/${encodeURIComponent(accountId)}/unhide`,
        body: {},
      })
    },
  }
}

function toAccount(response: Schemas['AccountResponse']): Account {
  return {
    id: response.id,
    code: response.code,
    accountClass: response.accountClass,
    type: response.type,
    names: response.names,
    currency: response.currency,
    parentId: response.parentId,
    icon: response.icon,
    color: response.color,
    isPostable: response.isPostable,
    isGroup: response.isGroup,
    isSystem: response.isSystem,
    hidden: response.hidden,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
    archivedAt: response.archivedAt === undefined ? undefined : parseInstant(response.archivedAt),
  }
}

function toAccountNode(node: Schemas['AccountNode']): AccountNode {
  return { account: toAccount(node.account), children: node.children.map(toAccountNode) }
}
